import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import { defineStyles, useStyles } from '../hooks/useStyles';
import FollowUserButton from "../users/FollowUserButton";
import UserMetaInfo from "../users/UserMetaInfo";
import { userGetProfileUrl } from '../../lib/collections/users/helpers';
import ContentStyles from "../common/ContentStyles";
import { commentBodyStyles } from '@/themes/stylePiping';
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import Loading from "../vulcan-core/Loading";
import TagSmallPostLink from '../tagging/TagSmallPostLink';
import { AnalyticsContext } from '../../lib/analyticsEvents';
import { useCurrentUserId } from '../common/withUser';
import { useNavigate } from '../../lib/routeUtil';

const UserRecentPostsQuery = gql(`
  query UserRecentPostsForCompactCard($selector: PostSelector, $limit: Int, $enableTotal: Boolean) {
    posts(selector: $selector, limit: $limit, enableTotal: $enableTotal) {
      results {
        ...PostsList
      }
    }
  }
`);

const styles = defineStyles("UltraFeedSuggestedUserCard", (theme: ThemeType) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    boxSizing: 'border-box',
    height: 152,
    fontSize: 14,
    fontWeight: 450,
    lineHeight: "19.5px",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.panelBackground.default,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    padding: 16,
    minHeight: 140,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease',
    cursor: 'pointer',
    ...theme.typography.postStyle,
    '&:hover': {
      boxShadow: theme.palette.boxShadow.default,
    },
  },
  nameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    gap: 8,
  },
  name: {
    fontSize: "1.8rem",
    fontWeight: 400,
    lineHeight: "1.2",
    color: theme.palette.grey["A400"],
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  nameLink: {
    color: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.7,
    },
  },
  followButtonWrapper: {
    flexShrink: 0,
  },
  metaRow: {
    fontSize: "0.9rem",
    marginBottom: 8,
    color: theme.palette.grey[600],
    '& .UserMetaInfo-info': {
      marginRight: 12,
    },
  },
  bio: {
    marginBottom: 8,
    lineHeight: "1.2rem",
    fontSize: "0.9rem",
  },
  bioText: {
    ...commentBodyStyles(theme),
    marginTop: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& p': {
      margin: 0,
    },
    '& p:first-child': {
      marginTop: 0,
    },
  },
  
  recentPosts: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    paddingTop: 4,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  postWrapper: {
    position: 'relative',
    width: '100%',
    height: 'auto',
    overflow: 'hidden',
    display: 'table',
    tableLayout: 'fixed',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  }
}));

const UltraFeedSuggestedUserCard = ({ 
  user, 
  onFollowToggle
}: {
  user: UsersMinimumInfo;
  onFollowToggle?: (user: UsersMinimumInfo) => void;
}) => {
  const classes = useStyles(styles);
  const followButtonRef = useRef<HTMLDivElement>(null);
  const currentUserId = useCurrentUserId();
  const navigate = useNavigate();
  
  const { htmlBio, displayName } = user;
  const profileUrl = userGetProfileUrl(user);
  const hasBio = !!(htmlBio && htmlBio.trim().length > 0);
  
  const { data: postsData, loading: postsLoading } = useQuery(UserRecentPostsQuery, {
    variables: {
      selector: { userPosts: { userId: user._id, sortedBy: "new" } },
      limit: 2, // Fetch 2 posts for card
      enableTotal: false,
    },
    skip: hasBio,
    notifyOnNetworkStatusChange: true,
  });
  
  const posts = postsData?.posts?.results;

  const handleFollowClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // if user is logged out, the FollowUserButton will open a login popup modal
    if (currentUserId) {
      onFollowToggle?.(user);
    }
  }, [currentUserId, user, onFollowToggle]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Check if the click was on a nested link or button - if so, let it handle the navigation
    const target = e.target as HTMLElement;
    if (target.closest('a, button')) {
      return;
    }
    navigate(profileUrl);
  }, [navigate, profileUrl]);

  if (!user?._id) {
    return <div className={classes.root}>User not found</div>;
  }

  
  return (
    <AnalyticsContext pageElementContext="suggestedUserCard" userIdDisplayed={user._id}>
      <div className={classes.root} onClick={handleCardClick}>
        <div className={classes.nameRow}>
          <div className={classNames(classes.name, classes.nameLink)}>
            {displayName}
          </div>
          <div 
            ref={followButtonRef}
            className={classes.followButtonWrapper}
            onClick={handleFollowClick}
          >
            <FollowUserButton 
              user={user} 
              styleVariant="ultraFeed"
            />
          </div>
        </div>
        
        <div className={classes.metaRow}>
          <UserMetaInfo 
            user={user} 
          />
        </div>
        
        {hasBio && (
          <ContentStyles className={classes.bio} contentType='postHighlight'>
            <div 
              className={classes.bioText} 
              dangerouslySetInnerHTML={{__html: htmlBio}}
            />
          </ContentStyles>
        )}
        
        {!hasBio && postsLoading && (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        )}
        
        {!hasBio && posts && posts.length > 0 && (
          <div className={classes.recentPosts}>
            {posts.slice(0, 2).map((post) => post && (
              <div key={post._id} className={classes.postWrapper}>
                <TagSmallPostLink
                  post={post}
                  hideAuthor
                  disableHoverPreview
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </AnalyticsContext>
  );

};

export default UltraFeedSuggestedUserCard;


